/* ──────────────────────────────────────────────────────────────────────
   ALUNOS EM RECUPERAÇÃO — Portal Equipe (DNACore V2)
   - Reusa o motor central (services/grade_engine.py) via /api/equipe/recuperacao
   - Lança nota via /api/equipe/recuperacao/nota
   - Status: EM_RECUPERACAO | RECUPERADO | NAO_RECUPERADO | AGUARDANDO
   ────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Loader2, RefreshCw, Save, Search, GraduationCap, AlertTriangle, CheckCircle2, XCircle, BookMarked, Send, Plus, X, ChevronDown, Printer, Heart, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/apiRecovery';

const TRIMESTRES = ['1º Trimestre', '2º Trimestre', '3º Trimestre'];
const fmt = (v) => (v == null ? '—' : Number(v).toFixed(1).replace('.', ','));

const STATUS_BADGE = {
  EM_RECUPERACAO: { label: 'Em recuperação', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  AGUARDANDO: { label: 'Aguardando nota', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: AlertTriangle },
  RECUPERADO: { label: 'Recuperado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  NAO_RECUPERADO: { label: 'Não recuperado', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
};

export default function RecuperacaoDialog({ open, onOpenChange, canEditAll = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    class_group_id: 'all',
    subject_id: 'all',
    period: 'all',
    q: '',
    year: new Date().getFullYear(),
  });
  const [drafts, setDrafts] = useState({}); // key -> nota digitada
  const [savingKey, setSavingKey] = useState(null);
  // Seleção para envio em lote ao Classroom
  const [selectedKeys, setSelectedKeys] = useState({}); // key -> bool
  const [classroomTarget, setClassroomTarget] = useState(null);
  // Histórico de envios
  const [showHistorico, setShowHistorico] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.class_group_id !== 'all') params.class_group_id = filters.class_group_id;
      if (filters.subject_id !== 'all') params.subject_id = filters.subject_id;
      if (filters.period !== 'all') params.period = filters.period;
      if (filters.q?.trim()) params.q = filters.q.trim();
      if (filters.year) params.year = filters.year;
      // eslint-disable-next-line no-console
      console.log('[Recuperação] GET /equipe/recuperacao', params);
      const res = await api.get('/equipe/recuperacao', { params, timeout: 90000 });
      const data = res.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      if (data.warning) {
        toast.warning(data.warning, { duration: 9000 });
      }
      if (data.capped) {
        toast.info(`Exibindo 200 de ${data.total_before_cap} registros. Use filtros para refinar.`, { duration: 9000 });
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailMsg = (typeof detail === 'string') ? detail : (detail?.message || null);
      let msg;
      if (status === 401) msg = 'Sua sessão expirou. Faça login novamente.';
      else if (status === 403) msg = detailMsg || 'Sem permissão.';
      else if (status === 404) msg = 'Endpoint /api/equipe/recuperacao não encontrado.';
      else if (status >= 500) msg = `Erro ${status} no servidor: ${detailMsg || 'tente novamente.'}`;
      else if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) msg = 'A consulta demorou demais. Filtre por turma/disciplina/trimestre.';
      else if (!e?.response) msg = 'Não foi possível conectar ao servidor.';
      else msg = detailMsg || e?.message || 'Erro ao carregar recuperações.';
      // eslint-disable-next-line no-console
      console.error('[Recuperação] erro de carga', { url: e?.config?.url, status, code: e?.code, message: e?.message, detail });
      toast.error(msg, { duration: 9000 });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    // Se OAuth do Classroom acabou e havia um envio pendente, reabre o modal
    try {
      const raw = sessionStorage.getItem('classroom_recovery_pending');
      if (raw) {
        const data = JSON.parse(raw);
        // Aceita pendência por até 30 minutos
        if (data?.target && data?.ts && (Date.now() - data.ts) < 30 * 60 * 1000) {
          setClassroomTarget(data.target);
        } else {
          sessionStorage.removeItem('classroom_recovery_pending');
        }
      }
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const turmas = useMemo(() => {
    const map = new Map();
    items.forEach((i) => { if (i.class_group_id) map.set(i.class_group_id, i.class_group_name || i.class_group_id); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [items]);

  const disciplinas = useMemo(() => {
    const map = new Map();
    items.forEach((i) => { if (i.subject_id) map.set(i.subject_id, i.subject_name || i.subject_id); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [items]);

  const visibleItems = useMemo(() => items, [items]);

  const keyOf = (i) => `${i.student_id}|${i.subject_id}|${i.period}|${i.year}`;

  // ── Seleção em lote ────────────────────────────────────────────
  const allSelected = visibleItems.length > 0 && visibleItems.every((i) => selectedKeys[keyOf(i)]);
  const anySelected = visibleItems.some((i) => selectedKeys[keyOf(i)]);
  const selectedItems = visibleItems.filter((i) => selectedKeys[keyOf(i)]);

  const toggleAll = (checked) => {
    if (!checked) { setSelectedKeys({}); return; }
    const next = {};
    visibleItems.forEach((i) => { next[keyOf(i)] = true; });
    setSelectedKeys(next);
  };
  const toggleOne = (i, checked) => setSelectedKeys((prev) => ({ ...prev, [keyOf(i)]: checked }));

  const openBulkSend = (channel) => {
    if (!selectedItems.length) { toast.error('Selecione pelo menos um aluno.'); return; }
    const t = selectedItems[0];
    const mismatch = selectedItems.some((a) => a.class_group_id !== t.class_group_id || a.subject_id !== t.subject_id || a.period !== t.period);
    if (mismatch) { toast.error('Selecione alunos da mesma turma, disciplina e trimestre.'); return; }
    setClassroomTarget({
      channel,  // 'dnaconnect' | 'classroom' | 'both' | 'print'
      alunos: selectedItems.map((a) => ({ aluno_id: a.student_id, aluno_nome: a.student_name })),
      class_group_id: t.class_group_id,
      class_group_name: t.class_group_name,
      subject_id: t.subject_id,
      subject_name: t.subject_name,
      trimester: t.period,
    });
  };
  const openRowSend = (i, channel) => setClassroomTarget({
    channel,
    alunos: [{ aluno_id: i.student_id, aluno_nome: i.student_name }],
    class_group_id: i.class_group_id,
    class_group_name: i.class_group_name,
    subject_id: i.subject_id,
    subject_name: i.subject_name,
    trimester: i.period,
  });

  const handleSave = async (i) => {
    const k = keyOf(i);
    const v = drafts[k] ?? (i.recuperacao ?? '');
    const num = Number(String(v).replace(',', '.'));
    if (Number.isNaN(num) || num < 0 || num > 10) {
      toast.error('Nota deve ser número entre 0 e 10');
      return;
    }
    setSavingKey(k);
    try {
      const res = await api.post('/equipe/recuperacao/nota', {
        student_id: i.student_id,
        class_group_id: i.class_group_id,
        subject_id: i.subject_id,
        subject_name: i.subject_name,
        period: i.period,
        year: i.year,
        grade_value: num,
      });
      const data = res.data || {};
      toast.success(`Nota salva — ${STATUS_BADGE[data.status]?.label || data.status}`);
      setDrafts((d) => { const c = { ...d }; delete c[k]; return c; });
      // Atualiza linha sem refetch
      setItems((arr) => arr.map((x) => (keyOf(x) === k ? {
        ...x,
        recuperacao: data.recuperacao,
        media_pos_recuperacao: data.media_pos_recuperacao,
        status: data.status,
      } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar nota');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden p-0" data-testid="recuperacao-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-violet-600" />
            Alunos em Recuperação
            {canEditAll && (
              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-bold">
                {/* só admin/coord */}
                Visão completa
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Lista calculada pelo motor central de notas (APA + Trabalho + Prontidão + Av. Trimestral &lt; 6,0). Nota de recuperação alimenta boletim, Central de Notas e DNA Connect automaticamente.
          </DialogDescription>
        </DialogHeader>

        {/* Filtros */}
        <div className="px-5 py-3 border-b bg-slate-50/60 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Buscar aluno</label>
            <div className="relative">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Nome do aluno"
                className="pl-7 h-8 text-sm"
                data-testid="rec-filter-q"
              />
            </div>
          </div>
          <div className="min-w-[160px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Turma</label>
            <Select value={filters.class_group_id} onValueChange={(v) => setFilters((f) => ({ ...f, class_group_id: v }))}>
              <SelectTrigger className="h-8 text-sm" data-testid="rec-filter-turma"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {turmas.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Disciplina</label>
            <Select value={filters.subject_id} onValueChange={(v) => setFilters((f) => ({ ...f, subject_id: v }))}>
              <SelectTrigger className="h-8 text-sm" data-testid="rec-filter-disciplina"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {disciplinas.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Trimestre</label>
            <Select value={filters.period} onValueChange={(v) => setFilters((f) => ({ ...f, period: v }))}>
              <SelectTrigger className="h-8 text-sm" data-testid="rec-filter-tri"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TRIMESTRES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[90px]">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Ano</label>
            <Input
              type="number"
              value={filters.year}
              onChange={(e) => setFilters((f) => ({ ...f, year: Number(e.target.value) || new Date().getFullYear() }))}
              className="h-8 text-sm"
              data-testid="rec-filter-year"
            />
          </div>
          <Button onClick={load} disabled={loading} size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="rec-apply">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Atualizar
          </Button>
          <Button onClick={() => setShowHistorico(true)} size="sm" variant="outline" className="h-8 gap-1" data-testid="rec-historico-btn">
            <BookMarked className="w-3.5 h-3.5" />
            Histórico
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                disabled={selectedItems.length === 0}
                className="h-8 bg-purple-600 hover:bg-purple-700 text-white gap-1"
                data-testid="rec-bulk-send-btn"
                title="Enviar plano de recuperação"
              >
                <Send className="w-3.5 h-3.5" />
                Enviar
                {selectedItems.length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white/20 text-white text-[10px] font-bold px-1">
                    {selectedItems.length}
                  </span>
                )}
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuLabel>Como enviar?</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => openBulkSend('dnaconnect')} data-testid="rec-bulk-send-dnaconnect">
                <Heart className="w-4 h-4 mr-2 text-pink-600" /> Enviar via DNA Connect
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openBulkSend('classroom')} data-testid="rec-bulk-send-classroom">
                <GraduationCap className="w-4 h-4 mr-2 text-purple-600" /> Enviar via Google Classroom
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openBulkSend('both')} data-testid="rec-bulk-send-both">
                <Send className="w-4 h-4 mr-2 text-emerald-600" /> Enviar via DNA Connect + Classroom
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openBulkSend('print')} data-testid="rec-bulk-send-print">
                <Printer className="w-4 h-4 mr-2 text-slate-600" /> Imprimir recuperação
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tabela */}
        <div className="overflow-auto max-h-[calc(92vh-200px)] px-2 py-2">
          {loading ? (
            <div className="p-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : visibleItems.length === 0 ? (
            <div className="p-12 text-center" data-testid="rec-empty">
              <BookMarked className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Nenhum aluno em recuperação para suas turmas neste trimestre.</p>
              <p className="text-xs text-slate-400 mt-1">Alunos com notas faltantes aparecem na IA de Pendências, não aqui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white z-10 border-b border-slate-200 text-slate-500 uppercase">
                  <tr>
                    <th className="px-2 py-2 w-8">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={(c) => toggleAll(!!c)}
                        aria-label="Selecionar todos"
                        data-testid="rec-select-all"
                        data-state={allSelected ? 'checked' : (anySelected ? 'indeterminate' : 'unchecked')}
                      />
                    </th>
                    <th className="text-left px-2 py-2">Aluno</th>
                    <th className="text-left px-2 py-2">Turma</th>
                    <th className="text-left px-2 py-2">Disciplina</th>
                    <th className="text-center px-1 py-2">Tri.</th>
                    <th className="text-center px-1 py-2" title="APA">APA</th>
                    <th className="text-center px-1 py-2" title="Trabalho">Trab</th>
                    <th className="text-center px-1 py-2" title="Prontidão">Pront</th>
                    <th className="text-center px-1 py-2" title="Avaliação Trimestral">Av.Trim</th>
                    <th className="text-center px-1 py-2 text-violet-700">Média</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-center px-1 py-2 w-[120px]">Recuperação</th>
                    <th className="text-center px-1 py-2 text-emerald-700">Pós Rec.</th>
                    <th className="text-center px-1 py-2">Classroom</th>
                    <th className="text-right px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.map((i) => {
                    const k = keyOf(i);
                    const draft = drafts[k];
                    const inputVal = draft !== undefined ? draft : (i.recuperacao ?? '');
                    const sb = STATUS_BADGE[i.status] || STATUS_BADGE.EM_RECUPERACAO;
                    const SbIcon = sb.Icon;
                    return (
                      <tr key={k} className="hover:bg-violet-50/30" data-testid={`rec-row-${i.student_id}-${i.subject_id}-${i.period}`}>
                        <td className="px-2 py-1.5 text-center">
                          <Checkbox
                            checked={!!selectedKeys[k]}
                            onCheckedChange={(c) => toggleOne(i, !!c)}
                            aria-label={`Selecionar ${i.student_name}`}
                            data-testid={`rec-select-${i.student_id}-${i.subject_id}-${i.period}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 font-medium text-slate-800">{i.student_name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{i.class_group_name || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600">{i.subject_name || '—'}</td>
                        <td className="px-1 py-1.5 text-center text-slate-700 whitespace-nowrap">{i.period?.replace(' Trimestre', 'T')}</td>
                        <td className="px-1 py-1.5 text-center">{fmt(i.notas?.APA)}</td>
                        <td className="px-1 py-1.5 text-center">{fmt(i.notas?.Trabalho)}</td>
                        <td className="px-1 py-1.5 text-center">{fmt(i.notas?.['Prontidão'])}</td>
                        <td className="px-1 py-1.5 text-center">{fmt(i.notas?.['Avaliação Trimestral'])}</td>
                        <td className="px-1 py-1.5 text-center font-bold text-violet-700">{fmt(i.media_final)}</td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${sb.cls}`}>
                            <SbIcon className="w-3 h-3" />
                            {sb.label}
                          </span>
                        </td>
                        <td className="px-1 py-1.5 text-center">
                          <Input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={inputVal}
                            onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
                            className="h-7 w-16 text-center text-sm mx-auto"
                            data-testid={`rec-input-${i.student_id}-${i.subject_id}-${i.period}`}
                          />
                        </td>
                        <td className="px-1 py-1.5 text-center font-semibold text-emerald-700">{fmt(i.media_pos_recuperacao)}</td>
                        <td className="px-1 py-1.5 text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-purple-700 border-purple-300 hover:bg-purple-50"
                                data-testid={`rec-send-${i.student_id}-${i.subject_id}-${i.period}`}
                                title="Enviar plano de recuperação"
                              >
                                <Send className="w-3 h-3" />
                                Enviar
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white">
                              <DropdownMenuItem onClick={() => openRowSend(i, 'dnaconnect')}>
                                <Heart className="w-4 h-4 mr-2 text-pink-600" /> DNA Connect
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openRowSend(i, 'classroom')}>
                                <GraduationCap className="w-4 h-4 mr-2 text-purple-600" /> Classroom
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openRowSend(i, 'both')}>
                                <Send className="w-4 h-4 mr-2 text-emerald-600" /> Ambos
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openRowSend(i, 'print')}>
                                <Printer className="w-4 h-4 mr-2 text-slate-600" /> Imprimir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button
                            size="sm"
                            onClick={() => handleSave(i)}
                            disabled={savingKey === k}
                            className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`rec-save-${i.student_id}-${i.subject_id}-${i.period}`}
                          >
                            {savingKey === k ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            Salvar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {classroomTarget && (
          <ClassroomEnvioModal
            target={classroomTarget}
            onClose={() => setClassroomTarget(null)}
            onSent={() => { setClassroomTarget(null); setSelectedKeys({}); }}
          />
        )}
        {showHistorico && (
          <HistoricoRecuperacoesDialog
            onClose={() => setShowHistorico(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────
// Impressão do plano de recuperação (layout institucional)
// ─────────────────────────────────────────────────────────────────
const PRINT_DOC_TITLE = 'Orientação de Estudos — Recuperação Trimestral';

function printRecuperacao(target, payload) {
  try {
    const w = window.open('', 'imprimir_recuperacao', 'width=900,height=900');
    if (!w) { toast.error('Pop-up bloqueado. Permita pop-ups para imprimir.'); return; }
    const escape = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const conteudosHtml = (payload.study_contents || []).map((c) => `<li><b>[${escape(c.tipo)}]</b> ${escape(c.valor)}</li>`).join('') || '<li class="muted">Nenhum conteúdo informado.</li>';
    const dueDate = payload.due_date ? new Date(payload.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
    const html = `
<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>${PRINT_DOC_TITLE} — ${escape(target.subject_name || '')}</title>
<style>
@page { size: A4; margin: 22mm 18mm; }
* { box-sizing: border-box; }
body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1f2937; line-height: 1.5; margin: 0; }
header { display:flex; align-items:center; gap:14px; border-bottom:2px solid #4f46e5; padding-bottom:10px; margin-bottom:18px; }
header .logo { width:48px; height:48px; border-radius:8px; background:linear-gradient(135deg,#7B3F98,#4f46e5); color:#fff; display:flex;align-items:center;justify-content:center;font-weight:700;font-size:18px; }
header h1 { margin:0; font-size:18px; color:#4338ca; }
header small { color:#6b7280; }
.grid { display:grid; grid-template-columns: 1fr 1fr; gap:8px 24px; margin-bottom:14px; }
.kv { font-size:13px; }
.kv b { display:block; color:#6b7280; font-size:11px; text-transform:uppercase; letter-spacing:.06em; }
section { margin-top:14px; }
section h2 { font-size:13px; color:#4338ca; text-transform:uppercase; letter-spacing:.06em; margin:0 0 6px; border-bottom:1px solid #e5e7eb; padding-bottom:4px; }
ul { padding-left:18px; margin:6px 0; }
.muted { color:#9ca3af; }
.alunos-list { columns: 2; column-gap: 24px; font-size: 13px; }
.alunos-list li { break-inside: avoid; }
.assinatura { margin-top:38px; display:grid; grid-template-columns:1fr 1fr; gap:36px; font-size:12px; color:#374151; }
.assinatura .linha { border-top:1px solid #1f2937; padding-top:6px; text-align:center; }
.print-btn { position:fixed; right:18px; top:18px; background:#4f46e5; color:#fff; padding:8px 16px; border:none; border-radius:8px; cursor:pointer; }
@media print { .print-btn { display:none; } }
</style></head>
<body>
<button class="print-btn" onclick="window.print()">Imprimir</button>
<header>
  <div class="logo">DNA</div>
  <div>
    <h1>${PRINT_DOC_TITLE}</h1>
    <small>${escape(target.class_group_name || '')} — ${escape(target.subject_name || '')} — ${escape(target.trimester || '')}</small>
  </div>
</header>
<div class="grid">
  <div class="kv"><b>Turma</b>${escape(target.class_group_name || '')}</div>
  <div class="kv"><b>Disciplina</b>${escape(target.subject_name || '')}</div>
  <div class="kv"><b>Trimestre</b>${escape(target.trimester || '')}</div>
  <div class="kv"><b>Prazo</b>${escape(dueDate)}</div>
  <div class="kv"><b>Total de alunos</b>${(target.alunos || []).length}</div>
  <div class="kv"><b>Emitido em</b>${new Date().toLocaleDateString('pt-BR')}</div>
</div>
<section>
  <h2>Objetivo / Título</h2>
  <div>${escape(payload.title || '')}</div>
</section>
<section>
  <h2>Instruções</h2>
  <div>${escape(payload.instructions || '—').replace(/\n/g,'<br/>')}</div>
</section>
<section>
  <h2>Conteúdos para estudar</h2>
  <ul>${conteudosHtml}</ul>
</section>
<section>
  <h2>Alunos contemplados</h2>
  <ul class="alunos-list">${(target.alunos || []).map((a) => `<li>${escape(a.aluno_nome || a.aluno_id)}</li>`).join('')}</ul>
</section>
<div class="assinatura">
  <div class="linha">Assinatura do Professor(a)</div>
  <div class="linha">Assinatura do Responsável</div>
</div>
<script>setTimeout(() => window.print(), 250);</script>
</body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch (e) {
    toast.error('Falha ao abrir janela de impressão.');
  }
}

// ─────────────────────────────────────────────────────────────────
// MODAL: Envio de Recuperação (DNA Connect | Classroom | Both | Print)
// ─────────────────────────────────────────────────────────────────
const STUDY_TIPOS = [
  { value: 'capitulo', label: 'Capítulo' },
  { value: 'pagina', label: 'Página' },
  { value: 'tema', label: 'Tema' },
  { value: 'lista_exercicios', label: 'Lista de exercícios' },
  { value: 'observacao', label: 'Observação' },
];

function ClassroomEnvioModal({ target, onClose, onSent }) {
  // Restaura form salvo antes do OAuth (se existir e for desta mesma turma+disc)
  const restored = (() => {
    try {
      const raw = sessionStorage.getItem('classroom_recovery_pending');
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (
        d?.target?.class_group_id === target.class_group_id &&
        d?.target?.subject_id === target.subject_id &&
        d?.target?.trimester === target.trimester
      ) {
        return d.form || null;
      }
    } catch (_) {}
    return null;
  })();
  const channel = target.channel || 'classroom';
  const channelMeta = (() => {
    if (channel === 'dnaconnect') return { label: 'DNA Connect (família)', Icon: Heart, color: 'pink' };
    if (channel === 'both') return { label: 'DNA Connect + Classroom', Icon: Send, color: 'emerald' };
    if (channel === 'print') return { label: 'Impressão', Icon: Printer, color: 'slate' };
    return { label: 'Google Classroom', Icon: GraduationCap, color: 'purple' };
  })();
  const [title, setTitle] = useState(restored?.title || `Recuperação - ${target.subject_name || ''} (${target.trimester || ''})`.trim());
  const [instructions, setInstructions] = useState(restored?.instructions || '');
  const [dueDate, setDueDate] = useState(restored?.dueDate || '');
  const [contents, setContents] = useState(restored?.contents || [{ tipo: 'capitulo', valor: '' }]);
  const [sending, setSending] = useState(false);
  const [scopeMissing, setScopeMissing] = useState(null); // { code, message, auth_url }
  const [needLink, setNeedLink] = useState(null); // { available_courses, message }
  const [pickingCourse, setPickingCourse] = useState(false);
  const [chosenCourseId, setChosenCourseId] = useState('');
  const [linking, setLinking] = useState(false);
  // Wizard preflight (verifica turma + alunos antes do envio)
  const [preflight, setPreflight] = useState({ loading: false, ran: false, code: null, turma_linked: false, course_name: null, students: [], message: null });
  const runPreflight = React.useCallback(async () => {
    if (channel !== 'classroom' && channel !== 'both') return;
    setPreflight((p) => ({ ...p, loading: true }));
    try {
      const res = await api.post('/equipe/recuperacao/classroom/preflight', {
        class_group_id: target.class_group_id,
        subject_id: target.subject_id,
        student_ids: target.alunos.map((a) => a.aluno_id),
      });
      const d = res.data || {};
      setPreflight({
        loading: false, ran: true,
        code: d.code, turma_linked: !!d.turma_linked,
        course_name: d.course_name || null,
        students: d.students || [],
        message: d.message || null,
      });
    } catch (e) {
      setPreflight((p) => ({ ...p, loading: false, ran: true, code: 'ERROR', message: 'Falha ao verificar Classroom.' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, target.class_group_id, target.subject_id]);
  React.useEffect(() => { runPreflight(); }, [runPreflight]);
  const linkedStudents = preflight.students.filter((s) => s.status === 'linked');
  const missingStudents = preflight.students.filter((s) => s.status === 'missing');
  // FASE 1/2: status do Google Classroom (consultado ao montar)
  const [gcStatus, setGcStatus] = useState({ loading: true, connected: false, scopes_ok: false });

  const refreshGoogleStatus = React.useCallback(async (validate = false) => {
    try {
      const url = validate ? '/google/classroom/status?validate=1' : '/google/classroom/status';
      const res = await api.get(url, { timeout: 20000 });
      const d = res.data || {};
      // PATCH: estados independentes — google_connected ≠ classroom_authorized
      const googleConnected = !!d.google_connected;
      const classroomAuthorized = !!(d.classroom_authorized ?? d.classroom_scopes_ok);
      setGcStatus({
        loading: false,
        google_connected: googleConnected,
        classroom_authorized: classroomAuthorized,
        classroom_scopes_ok: classroomAuthorized,  // alias compat
        connected: googleConnected && classroomAuthorized,
        scopes_ok: classroomAuthorized,
        token_valid: !!d.token_valid,
        refresh_available: !!d.refresh_available,
        missing_scopes: d.missing_scopes || [],
        expires_at: d.expires_at || null,
        live_ok: d.live_ok,
        live_error: d.live_error,
      });
      return d;
    } catch (_) {
      setGcStatus({
        loading: false,
        google_connected: false,
        classroom_authorized: false,
        classroom_scopes_ok: false,
        connected: false,
        scopes_ok: false,
        token_valid: false,
        missing_scopes: [],
      });
      return null;
    }
  }, []);

  useEffect(() => { refreshGoogleStatus(); }, [refreshGoogleStatus]);

  const updateContent = (i, patch) => setContents((arr) => arr.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addContent = () => setContents((arr) => [...arr, { tipo: 'tema', valor: '' }]);
  const removeContent = (i) => setContents((arr) => arr.filter((_, idx) => idx !== i));

  const buildPayload = () => ({
    student_ids: target.alunos.map((a) => a.aluno_id),
    class_group_id: target.class_group_id,
    subject_id: target.subject_id,
    subject_name: target.subject_name,
    trimester: target.trimester,
    title: title.trim(),
    instructions: instructions.trim(),
    study_contents: contents.filter((c) => (c.valor || '').trim()).map((c) => ({ tipo: c.tipo, valor: c.valor.trim() })),
    due_date: dueDate || null,
  });

  const reconectarClassroom = async () => {
    try {
      // PATCH: short-circuit APENAS se Classroom realmente autorizado
      if (gcStatus.google_connected && gcStatus.classroom_authorized) {
        toast.info('Google Classroom já está autorizado. Clique em "Enviar Recuperação".', { duration: 5000 });
        return;
      }
      // 1) Salva contexto do envio para restaurar depois do OAuth
      try {
        sessionStorage.setItem('classroom_recovery_pending', JSON.stringify({
          target,
          form: { title, instructions, dueDate, contents },
          ts: Date.now(),
        }));
      } catch (_) { /* noop */ }

      // 2) Pede authorization_url. PATCH: SEMPRE com force=1 quando o usuário
      // clica "Autorizar Classroom" (o botão só aparece se NÃO está autorizado).
      // Isso descarta qualquer token stale do bug antigo (scopes salvos como
      // "completos" mas Google retornou granted parcial) e força nova OAuth.
      const ret = window.location.pathname + window.location.search + window.location.hash;
      const res = await api.get(`/classroom/connect?force=1&return_to=${encodeURIComponent(ret)}`);
      const data = res.data || {};

      // Mesmo com force=1, se backend retornar already_connected (caso raro
      // de race), atualiza estado e segue. Mas valida live antes de confiar.
      if (data.already_connected) {
        const fresh = await refreshGoogleStatus(true);
        if (fresh?.classroom_authorized) {
          toast.success('Google Classroom já estava autorizado. Clique em "Enviar Recuperação".', { duration: 6000 });
          return;
        }
        // Live disse que não → ignora already_connected e força OAuth via segunda chamada
        const res2 = await api.get(`/classroom/connect?force=1&return_to=${encodeURIComponent(ret)}`);
        const url2 = res2.data?.authorization_url;
        if (!url2) { toast.error('Não foi possível iniciar a autorização.'); return; }
        window.location.href = url2;
        return;
      }
      const authUrl = data.authorization_url;
      if (!authUrl) { toast.error('Não foi possível iniciar a autorização.'); return; }

      // 3) SAME-TAB redirect — preserva JWT do DNACore.
      window.location.href = authUrl;
    } catch (e) {
      toast.error('Erro ao iniciar autorização do Google Classroom.');
    }
  };

  // FASE 1: ao voltar do OAuth (mesma aba, ?classroom_connected=1) NÃO envia
  // automaticamente. Apenas atualiza o status e exibe toast. Usuário clica
  // em "Enviar Recuperação" para concluir (separação 2 fases).
  // PATCH: faz polling até confirmar token persistido (evita race do callback).
  useEffect(() => {
    let cancelled = false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('classroom_connected') === '1') {
        setScopeMissing(null);
        params.delete('classroom_connected');
        const newSearch = params.toString();
        window.history.replaceState({}, '',
          window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash);
        // Polling: re-consulta status (com validate=1) até classroom_scopes_ok
        const tryConfirm = async (attempt = 0) => {
          if (cancelled) return;
          const data = await refreshGoogleStatus(true); // PATCH: validate live
          if (data?.classroom_authorized || data?.classroom_scopes_ok) {
            toast.success('Google Classroom autorizado. Clique em "Enviar Recuperação".', { duration: 8000 });
            return;
          }
          if (attempt < 8) {
            setTimeout(() => tryConfirm(attempt + 1), 600);
          } else {
            const miss = (data?.missing_scopes || []).map(s => s.replace('https://www.googleapis.com/auth/', '')).join(', ');
            toast.warning(`Classroom não autorizado. Marque TODOS os checkboxes na tela do Google. Escopos faltantes: ${miss || 'desconhecidos'}.`, { duration: 14000 });
          }
        };
        tryConfirm(0);
      }
    } catch (_) { /* noop */ }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirSeletorCurso = async () => {
    setPickingCourse(true);
    setChosenCourseId('');
    try {
      // Se já recebemos a lista no erro, usa ela. Senão, busca.
      if (!needLink?.available_courses) {
        const res = await api.get('/equipe/recuperacao/classroom/courses');
        const code = res.data?.code;
        const courses = res.data?.courses || [];
        if (code !== 'OK') {
          // PATCH: trata mensagem específica do backend (institucional / API error)
          toast.error(res.data?.message || 'Não foi possível listar cursos do Classroom.', { duration: 9000 });
          setPickingCourse(false);
          return;
        }
        setNeedLink((prev) => ({ ...(prev || { message: 'Selecione o curso do Classroom para vincular esta turma.' }), available_courses: courses }));
      }
    } catch (e) {
      toast.error('Erro ao listar cursos do Classroom.');
      setPickingCourse(false);
    }
  };

  const vincularCurso = async () => {
    if (!chosenCourseId) { toast.error('Selecione um curso.'); return; }
    setLinking(true);
    try {
      await api.post('/equipe/recuperacao/classroom/link', {
        class_group_id: target.class_group_id,
        subject_id: target.subject_id,
        classroom_course_id: chosenCourseId,
      });
      // PATCH: se foi disparado por erro de envio, reenvia. Se foi botão proativo, só fecha.
      const wasFromError = needLink?.fromError === true;
      setNeedLink(null);
      setPickingCourse(false);
      if (wasFromError) {
        toast.success('Curso vinculado. Enviando atividade...');
        await enviar();
      } else {
        toast.success('Turma vinculada ao Google Classroom com sucesso.', { duration: 6000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === 'string') ? detail : (detail?.message || 'Falha ao vincular curso.');
      toast.error(msg);
    } finally {
      setLinking(false);
    }
  };

  const enviar = async (opts = {}) => {
    const allowPartial = !!opts.allowPartial;
    if (!title.trim()) { toast.error('Informe o título da atividade.'); return; }
    setSending(true);
    setScopeMissing(null);
    try {
      const payload = buildPayload();
      if (allowPartial) {
        // Envia somente alunos com vínculo no Classroom
        const ids = linkedStudents.map((s) => s.id);
        if (ids.length === 0) { toast.error('Nenhum aluno pronto para envio.'); setSending(false); return; }
        payload.student_ids = ids;
        payload.allow_partial = true;
      }
      const channel = target.channel || 'classroom';
      if (channel === 'print') {
        // Abre janela de impressão com layout institucional
        printRecuperacao(target, payload);
        // Audit best-effort (não bloqueia impressão)
        try {
          await api.post('/recovery/audit-print', {
            class_group_id: target.class_group_id,
            subject_id: target.subject_id,
            subject_name: target.subject_name,
            trimester: target.trimester,
            student_ids: payload.student_ids,
            title: payload.title,
          });
        } catch (_) { /* noop */ }
        toast.success('Plano de recuperação aberto para impressão.');
        try { sessionStorage.removeItem('classroom_recovery_pending'); } catch (_) {}
        onSent();
        return;
      }
      const errors = [];
      let okCount = 0;
      if (channel === 'dnaconnect' || channel === 'both') {
        try {
          await api.post('/recovery/send-dnaconnect', payload);
          okCount++;
        } catch (e) {
          const detail = e?.response?.data?.detail;
          const msg = (typeof detail === 'string') ? detail : (detail?.message || 'Falha no DNA Connect.');
          errors.push({ channel: 'DNA Connect', message: msg });
        }
      }
      if (channel === 'classroom' || channel === 'both') {
        try {
          await api.post('/equipe/recuperacao/classroom', payload);
          okCount++;
        } catch (e) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          const code = detail?.code;
          const msg = (typeof detail === 'string') ? detail : (detail?.message || 'Falha no Classroom.');
          if ((status === 403 || status === 503) && (code === 'CLASSROOM_INSTITUTIONAL_NOT_CONNECTED' || code === 'CLASSROOM_NOT_AUTHORIZED' || code === 'CLASSROOM_SCOPE_MISSING' || code === 'CLASSROOM_NOT_CONNECTED')) {
            // PATCH FINAL: conta institucional. Mostra mensagem direta — professor não autoriza.
            toast.error(msg, { duration: 14000 });
            errors.push({ channel: 'Google Classroom', message: msg, code });
          } else if ((status === 503 || status === 400) && code === 'CLASSROOM_COURSE_NOT_LINKED') {
            setNeedLink({ message: msg, available_courses: detail?.available_courses || null, fromError: true });
            errors.push({ channel: 'Google Classroom', message: msg, code });
          } else if (status === 400 && detail?.invalidos) {
            errors.push({ channel: 'Google Classroom', message: `${msg} (${detail.invalidos.length} aluno(s) inválido(s))` });
          } else {
            errors.push({ channel: 'Google Classroom', message: msg });
          }
        }
      }

      if (errors.length === 0) {
        const labels = channel === 'both' ? 'DNA Connect + Classroom' : (channel === 'dnaconnect' ? 'DNA Connect' : 'Google Classroom');
        toast.success(`✓ Recuperação enviada via ${labels} para ${payload.student_ids.length} aluno(s).`, { duration: 6000 });
        try { sessionStorage.removeItem('classroom_recovery_pending'); } catch (_) {}
        try { localStorage.removeItem('classroom_oauth_just_connected'); } catch (_) {}
        onSent();
      } else if (okCount > 0) {
        // Sucesso parcial (combinado)
        toast.warning(`Envio parcial: ${okCount} canal(is) ok. Falhou: ${errors.map((e) => `${e.channel} (${e.message})`).join(' | ')}`, { duration: 9000 });
      } else {
        // Tudo falhou — mostra detalhe canal a canal
        if (errors.length === 1) {
          toast.error(`${errors[0].channel}: ${errors[0].message}`, { duration: 7000 });
        } else {
          toast.error(`Falhas: ${errors.map((e) => `${e.channel} (${e.message})`).join(' | ')}`, { duration: 9000 });
        }
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) { try { sessionStorage.removeItem('classroom_recovery_pending'); } catch (_) {} onClose(); } }}>
      <DialogContent className="max-w-2xl bg-white" data-testid="rec-classroom-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <channelMeta.Icon className={`w-5 h-5 text-${channelMeta.color}-600`} />
            Recuperação — {channelMeta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* PATCH FINAL: conta institucional — banner removido.
              Mensagens de erro vêm direto do backend via toast. */}
          {false && (channel === 'classroom' || channel === 'both') && !gcStatus.loading
            && gcStatus.google_connected && !gcStatus.classroom_authorized && (
            <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900" data-testid="rec-classroom-scope-warn">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-600" />
                <div className="flex-1">
                  <div className="font-semibold">Google conectado, mas Classroom não autorizado</div>
                  <div className="text-xs text-orange-800 mt-0.5">
                    {gcStatus.missing_scopes && gcStatus.missing_scopes.length > 0
                      ? `Escopos faltantes: ${gcStatus.missing_scopes.map((s) => s.replace('https://www.googleapis.com/auth/', '')).join(', ')}`
                      : 'Sua conta Google não tem permissão para enviar atividades ao Classroom.'}
                  </div>
                  <div className="mt-2">
                    <Button size="sm" onClick={reconectarClassroom} className="h-7 bg-orange-600 hover:bg-orange-700 text-white gap-1" data-testid="rec-modal-autorizar-btn">
                      <GraduationCap className="w-3.5 h-3.5" />
                      Autorizar Classroom
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {scopeMissing && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="rec-classroom-scope-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600" />
                <div className="flex-1">
                  <div className="font-semibold">Google Classroom precisa de autorização</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    {scopeMissing.message || 'Sua conta Google ainda não autorizou os escopos do Classroom para enviar atividades.'}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" onClick={reconectarClassroom} className="h-7 bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="rec-modal-reconectar-btn">
                      <GraduationCap className="w-3.5 h-3.5" />
                      Reconectar Google Classroom
                    </Button>
                    <span className="text-[11px] text-amber-700">Após autorizar, volte aqui e clique em "Enviar para Classroom".</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* PATCH: botão proativo "Vincular curso" — visível sempre que canal=classroom
              e a turma ainda não foi vinculada nesta sessão. Permite vincular ANTES de tentar enviar. */}
          {(channel === 'classroom' || channel === 'both') && !needLink && !pickingCourse && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm flex items-center justify-between gap-3" data-testid="rec-classroom-link-proactive">
              <div className="flex items-center gap-2">
                <BookMarked className="w-4 h-4 text-slate-600" />
                <span className="text-slate-700">Turma sem curso do Classroom vinculado? Vincule antes de enviar.</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setNeedLink({ message: 'Selecione o curso do Classroom para vincular esta turma.', available_courses: null, fromError: false }); abrirSeletorCurso(); }}
                className="h-7 gap-1"
                data-testid="rec-modal-vincular-proativo-btn"
              >
                <GraduationCap className="w-3.5 h-3.5" />
                Vincular curso do Classroom
              </Button>
            </div>
          )}
          {needLink && !pickingCourse && (
            <div className="rounded-md border border-purple-300 bg-purple-50 p-3 text-sm text-purple-900" data-testid="rec-classroom-link-banner">
              <div className="flex items-start gap-2">
                <BookMarked className="w-4 h-4 mt-0.5 text-purple-600" />
                <div className="flex-1">
                  <div className="font-semibold">Turma não vinculada a um curso do Classroom</div>
                  <div className="text-xs text-purple-800 mt-0.5">
                    {needLink.message || 'Vincule o curso do Google Classroom a esta turma antes de enviar.'}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" onClick={abrirSeletorCurso} className="h-7 bg-purple-600 hover:bg-purple-700 text-white gap-1" data-testid="rec-modal-vincular-btn">
                      <GraduationCap className="w-3.5 h-3.5" />
                      Vincular curso do Classroom
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {needLink && pickingCourse && (
            <div className="rounded-md border border-purple-300 bg-white p-3 text-sm space-y-2" data-testid="rec-classroom-course-picker">              <div className="font-semibold text-purple-800">Escolha o curso do Classroom desta turma</div>
              {(needLink.available_courses || []).length === 0 ? (
                <div className="text-xs text-slate-600">Nenhum curso ativo encontrado na sua conta Google. Crie a turma no Classroom e tente novamente.</div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {(needLink.available_courses || []).map((c) => (
                    <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer ${chosenCourseId === c.id ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <input
                        type="radio"
                        name="classroom-course"
                        value={c.id}
                        checked={chosenCourseId === c.id}
                        onChange={(e) => setChosenCourseId(e.target.value)}
                        data-testid={`rec-course-option-${c.id}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-800">{c.name}</div>
                        {c.section && <div className="text-xs text-slate-500">{c.section}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => { setPickingCourse(false); }} disabled={linking}>Cancelar</Button>
                <Button size="sm" onClick={vincularCurso} disabled={!chosenCourseId || linking} className="bg-purple-600 hover:bg-purple-700 text-white gap-1" data-testid="rec-modal-confirmar-vinculo-btn">
                  {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GraduationCap className="w-3.5 h-3.5" />}
                  {linking ? 'Vinculando...' : 'Vincular e enviar'}
                </Button>
              </div>
            </div>
          )}
          {/* WIZARD PREFLIGHT — status visual da turma + alunos */}
          {(channel === 'classroom' || channel === 'both') && (
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm space-y-2" data-testid="rec-classroom-preflight">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-800 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-purple-600" />
                  Verificação do Classroom
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={runPreflight}
                  disabled={preflight.loading}
                  className="h-7 gap-1"
                  data-testid="rec-preflight-sync-btn"
                >
                  {preflight.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Sincronizar agora
                </Button>
              </div>
              {/* Status turma */}
              <div className="flex items-center gap-2 text-xs">
                {preflight.turma_linked ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">
                      Turma vinculada{preflight.course_name ? ` a ${preflight.course_name}` : ''}.
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-700">
                      {preflight.message || 'Turma sem curso do Classroom vinculado.'}
                    </span>
                  </>
                )}
              </div>
              {/* Status alunos */}
              {preflight.ran && preflight.students.length > 0 && (
                <div className="space-y-1 max-h-44 overflow-y-auto pr-1" data-testid="rec-preflight-students">
                  {preflight.students.map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded border ${s.status === 'linked' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
                      data-testid={`rec-preflight-student-${s.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {s.status === 'linked' ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-slate-800">{s.name}</div>
                          {s.status === 'missing' && (
                            <div className="text-[10px] text-amber-700 truncate">
                              {s.has_email
                                ? 'Aluno com e-mail cadastrado, mas não matriculado neste curso do Classroom.'
                                : 'Aluno sem e-mail institucional no cadastro.'}
                            </div>
                          )}
                          {s.status === 'linked' && s.matched_now && (
                            <div className="text-[10px] text-emerald-700">Vinculado automaticamente por e-mail.</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {s.status === 'missing' && (
                          <a
                            href={`/v2/aluno/${s.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-blue-700 hover:underline"
                            data-testid={`rec-preflight-open-student-${s.id}`}
                          >
                            Abrir perfil
                          </a>
                        )}
                        <span className={`text-[10px] uppercase tracking-wide ${s.status === 'linked' ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {s.status === 'linked' ? 'Vinculado' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Resumo */}
              {preflight.ran && (
                <div className="text-[11px] text-slate-600 pt-1 border-t border-slate-100">
                  {linkedStudents.length} pronto(s) para envio · {missingStudents.length} pendente(s)
                  {missingStudents.length > 0 && (
                    <span className="block mt-0.5">
                      Sem vínculo: {missingStudents.map((s) => s.name).join(', ')}.{' '}
                      Use "Sincronizar agora" após adicionar email institucional ao aluno.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div><span className="font-medium">Turma:</span> {target.class_group_name}</div>
            <div><span className="font-medium">Disciplina:</span> {target.subject_name}</div>
            <div><span className="font-medium">Trimestre:</span> {target.trimester}</div>
            <div className="mt-2"><span className="font-medium">Alunos ({target.alunos.length}):</span></div>
            <ul className="mt-1 list-disc list-inside max-h-32 overflow-y-auto text-slate-700">
              {target.alunos.map((a, i) => (<li key={i}>{a.aluno_nome}</li>))}
            </ul>
          </div>
          <div>
            <Label>Título da atividade *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Recuperação 1º Trim — Matemática" data-testid="rec-modal-title" />
          </div>
          <div>
            <Label>Instruções para os alunos</Label>
            <Textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Como se preparar, o que será cobrado, formato da prova/trabalho..." data-testid="rec-modal-instructions" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Conteúdos para estudar</Label>
              <Button type="button" size="sm" variant="outline" onClick={addContent} className="gap-1" data-testid="rec-modal-add-content"><Plus className="w-3.5 h-3.5" />Adicionar</Button>
            </div>
            <div className="space-y-2">
              {contents.map((c, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`rec-modal-content-${i}`}>
                  <Select value={c.tipo} onValueChange={(v) => updateContent(i, { tipo: v })}>
                    <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {STUDY_TIPOS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input className="flex-1" value={c.valor} onChange={(e) => updateContent(i, { valor: e.target.value })} placeholder="Detalhe (ex.: Cap. 5; pgs 80-95)" />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeContent(i)} aria-label="Remover">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {contents.length === 0 && (<div className="text-xs text-slate-500">Clique em "Adicionar" para incluir conteúdos.</div>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prazo (data limite)</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="rec-modal-due-date" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending || linking}>Cancelar</Button>
          {(channel === 'classroom' || channel === 'both') && preflight.ran && missingStudents.length > 0 && linkedStudents.length > 0 && (
            <Button
              onClick={() => enviar({ allowPartial: true })}
              disabled={sending}
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-2"
              data-testid="rec-modal-enviar-parcial-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Enviando...' : `Enviar os ${linkedStudents.length} prontos`}
            </Button>
          )}
          {/* PATCH FINAL: conta institucional — professor SEMPRE vê "Enviar".
              Sem botão de autorizar, sem OAuth no fluxo do professor.
              Se conta institucional não estiver conectada, backend retorna 403
              e frontend mostra mensagem direcionada ao admin. */}
          <Button
            onClick={() => enviar()}
            disabled={sending || ((channel === 'classroom' || channel === 'both') && preflight.ran && missingStudents.length > 0)}
            className={`bg-${channelMeta.color}-600 hover:bg-${channelMeta.color}-700 text-white gap-2`}
            data-testid="rec-modal-enviar-btn"
          >
            <channelMeta.Icon className="w-4 h-4" />
            {sending ? 'Enviando...' : (channel === 'print' ? 'Imprimir agora' : (channel === 'classroom' ? 'Enviar Recuperação' : `Enviar via ${channelMeta.label}`))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────
// HISTÓRICO de Recuperações Enviadas (lista + ações)
// ─────────────────────────────────────────────────────────────────
const HIST_STATUS_BADGE = {
  enviada: { label: 'Enviada', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  aberta: { label: 'Aberta', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  concluida: { label: 'Concluída', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelado: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function HistoricoRecuperacoesDialog({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // envio_id em edição
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recovery/historico');
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Erro ao carregar histórico.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancelar = async (it) => {
    if (!window.confirm(`Cancelar a recuperação "${it.title || ''}"? Será removida do DNA Connect.`)) return;
    setBusyId(it.envio_id);
    try {
      const res = await api.post(`/recovery/${it.envio_id}/cancelar`);
      toast.success('Recuperação cancelada.');
      if (res.data?.notice) toast.info(res.data.notice, { duration: 8000 });
      await load();
    } catch (e) {
      toast.error('Falha ao cancelar.');
    } finally { setBusyId(null); }
  };

  const reenviar = async (it, channels) => {
    setBusyId(it.envio_id);
    try {
      const res = await api.post(`/recovery/${it.envio_id}/reenviar`, { channels });
      const r = res.data?.results || {};
      const okCh = []; const failCh = [];
      if (channels.includes('dnaconnect')) (r.dnaconnect?.ok ? okCh : failCh).push('DNA Connect');
      if (channels.includes('classroom')) (r.classroom?.ok ? okCh : failCh).push('Classroom');
      if (failCh.length === 0) toast.success(`✓ Reenviado via ${okCh.join(' + ')}.`, { duration: 6000 });
      else if (okCh.length > 0) toast.warning(`Parcial: ok=${okCh.join('+')} | falhou=${failCh.join('+')}`, { duration: 8000 });
      else toast.error(`Falha ao reenviar: ${failCh.join('+')}`);
      await load();
    } catch (e) {
      toast.error('Falha ao reenviar.');
    } finally { setBusyId(null); }
  };

  const imprimir = (it) => {
    printRecuperacao(
      {
        class_group_id: it.class_group_id,
        class_group_name: it.class_group_name,
        subject_id: it.subject_id,
        subject_name: it.subject_name,
        trimester: it.trimester,
        alunos: (it.students || []).map((s) => ({ aluno_id: s.id, aluno_nome: s.name })),
      },
      {
        title: it.title,
        instructions: it.instructions,
        study_contents: it.study_contents || [],
        due_date: it.due_date,
        student_ids: (it.students || []).map((s) => s.id),
      }
    );
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white" data-testid="historico-recuperacoes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-violet-600" />
            Histórico de Recuperações Enviadas
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10">Nenhuma recuperação enviada ainda.</div>
          ) : (
            items.map((it) => {
              const meta = HIST_STATUS_BADGE[it.status] || HIST_STATUS_BADGE.enviada;
              return (
                <div key={it.envio_id} className="border rounded-md p-3 bg-white" data-testid={`hist-row-${it.envio_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800">{it.title}</div>
                      <div className="text-xs text-slate-500">
                        {it.class_group_name} · {it.subject_name} · {it.trimester} · {it.students?.length || 0} aluno(s)
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Enviado em {it.created_at ? new Date(it.created_at).toLocaleString('pt-BR') : '—'}
                        {it.due_date && (<> · Prazo: {new Date(it.due_date + 'T00:00:00').toLocaleDateString('pt-BR')}</>)}
                        {it.channels && (<> · Canais: {(it.channels || []).join(', ')}</>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="h-7 gap-1" disabled={busyId === it.envio_id} data-testid={`hist-actions-${it.envio_id}`}>
                            {busyId === it.envio_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                            Ações
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => setEditing(it.envio_id)}>
                            <PenTool className="w-4 h-4 mr-2 text-blue-600" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => imprimir(it)}>
                            <Printer className="w-4 h-4 mr-2 text-slate-600" /> Visualizar / Imprimir
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => reenviar(it, ['dnaconnect'])}>
                            <Heart className="w-4 h-4 mr-2 text-pink-600" /> Reenviar (DNA Connect)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => reenviar(it, ['classroom'])}>
                            <GraduationCap className="w-4 h-4 mr-2 text-purple-600" /> Reenviar (Classroom)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => reenviar(it, ['dnaconnect', 'classroom'])}>
                            <Send className="w-4 h-4 mr-2 text-emerald-600" /> Reenviar (Ambos)
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => cancelar(it)} className="text-red-600 focus:bg-red-50">
                            <XCircle className="w-4 h-4 mr-2 text-red-600" /> Excluir / Cancelar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {it.instructions && (
                    <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{it.instructions}</div>
                  )}
                  {Array.isArray(it.study_contents) && it.study_contents.length > 0 && (
                    <ul className="list-disc list-inside text-xs text-slate-600 mt-1">
                      {it.study_contents.map((c, idx) => (<li key={idx}><b>[{c.tipo}]</b> {c.valor}</li>))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
        {editing && (
          <EditarRecuperacaoDialog
            envioId={editing}
            item={items.find((x) => x.envio_id === editing)}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditarRecuperacaoDialog({ envioId, item, onClose, onSaved }) {
  const [title, setTitle] = useState(item?.title || '');
  const [instructions, setInstructions] = useState(item?.instructions || '');
  const [contents, setContents] = useState(item?.study_contents || [{ tipo: 'capitulo', valor: '' }]);
  const [dueDate, setDueDate] = useState(item?.due_date || '');
  const [saving, setSaving] = useState(false);
  const updateC = (i, p) => setContents((arr) => arr.map((c, idx) => (idx === i ? { ...c, ...p } : c)));
  const addC = () => setContents((a) => [...a, { tipo: 'tema', valor: '' }]);
  const rmC = (i) => setContents((a) => a.filter((_, idx) => idx !== i));

  const salvar = async () => {
    if (!title.trim()) { toast.error('Título obrigatório.'); return; }
    setSaving(true);
    try {
      const res = await api.put(`/recovery/${envioId}`, {
        title: title.trim(),
        instructions,
        study_contents: contents.filter((c) => (c.valor || '').trim()).map((c) => ({ tipo: c.tipo, valor: c.valor.trim() })),
        due_date: dueDate || null,
      });
      toast.success('Recuperação atualizada no DNA Connect.');
      if (res.data?.notice) toast.info(res.data.notice, { duration: 9000 });
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Falha ao salvar.';
      toast.error(typeof msg === 'string' ? msg : (msg.message || 'Falha ao salvar.'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="editar-recuperacao-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenTool className="w-5 h-5 text-blue-600" /> Editar Recuperação</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label>Título</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-title" />
          </div>
          <div>
            <Label>Objetivo / Instruções</Label>
            <Textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Conteúdos / Atividades</Label>
              <Button type="button" size="sm" variant="outline" onClick={addC} className="gap-1"><Plus className="w-3.5 h-3.5" />Adicionar</Button>
            </div>
            <div className="space-y-2">
              {contents.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={c.tipo} onValueChange={(v) => updateC(i, { tipo: v })}>
                    <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {STUDY_TIPOS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input className="flex-1" value={c.valor} onChange={(e) => updateC(i, { valor: e.target.value })} placeholder="Detalhe" />
                  <Button type="button" size="icon" variant="ghost" onClick={() => rmC(i)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prazo</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="edit-save-btn">
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

